import { AnalyticsReportCreationSource } from "@redotech/crdb-enums/analytics/analytics-report-creation-source";
import { CustomAnalyticsQueryVersionSchema } from "@redotech/redo-analytics-custom-analytics/query/custom-analytics-report-query";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

export const getCustomReportsUsage = unaryRpc({
  name: "getCustomReportsUsage" as const,
  input: z.object({
    lookbackDays: z.number().int().min(1).max(365),
    lookbackEndOffsetDays: z.number().int().min(0).max(364).optional(),
    teamId: z.string().trim().optional(),
  }),
  output: z.object({
    lookbackDays: z.number().int().min(1).max(365),
    teamId: z.string().nullable(),
    totals: z.object({
      totalReports: z.number().int().min(0),
      readyReports: z.number().int().min(0),
      failedReports: z.number().int().min(0),
      unavailableReports: z.number().int().min(0),
      technicalFailureReports: z.number().int().min(0),
      preparingReports: z.number().int().min(0),
      uniqueTeams: z.number().int().min(0),
      uniqueCreators: z.number().int().min(0),
      successRate: z.number().min(0).max(1).nullable(),
    }),
    sourceBreakdown: z.array(
      z.object({
        source: z.enum(AnalyticsReportCreationSource),
        totalReports: z.number().int().min(0),
        readyReports: z.number().int().min(0),
        unavailableReports: z.number().int().min(0),
        technicalFailureReports: z.number().int().min(0),
        preparingReports: z.number().int().min(0),
        successRate: z.number().min(0).max(1).nullable(),
      }),
    ),
    queryVersionBreakdown: z.array(
      z.object({
        queryVersion: CustomAnalyticsQueryVersionSchema.nullable(),
        totalReports: z.number().int().min(0),
        readyReports: z.number().int().min(0),
        failedReports: z.number().int().min(0),
        preparingReports: z.number().int().min(0),
      }),
    ),
    daily: z.array(
      z.object({
        date: z.string(),
        totalReports: z.number().int().min(0),
        readyReports: z.number().int().min(0),
        failedReports: z.number().int().min(0),
        unavailableReports: z.number().int().min(0),
        technicalFailureReports: z.number().int().min(0),
        preparingReports: z.number().int().min(0),
        successRate: z.number().min(0).max(1).nullable(),
      }),
    ),
    failureCategories: z.array(
      z.object({
        category: z.string(),
        source: z.enum(AnalyticsReportCreationSource),
        count: z.number().int().min(0),
      }),
    ),
    recentFailures: z.array(
      z.object({
        rowId: z.string(),
        publicId: z.string(),
        teamId: z.string(),
        title: z.string().nullable(),
        requestedQuestion: z.string(),
        errorMessage: z.string().nullable(),
        failureCategory: z.string(),
        creationSource: z.enum(AnalyticsReportCreationSource),
        queryVersion: CustomAnalyticsQueryVersionSchema.nullable(),
        createdAt: z.date(),
        updatedAt: z.date(),
      }),
    ),
  }),
});

export type GetCustomReportsUsageInput = z.infer<
  typeof getCustomReportsUsage.input
>;
export type GetCustomReportsUsageOutput = z.infer<
  typeof getCustomReportsUsage.output
>;
